import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Card, CardContent, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Button, MenuItem, TextField, Pagination,
  Skeleton, Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../../services/api';

const C = {
  purple: '#552583',
  gold:   '#FDB927',
};

const fmt = (v) => (parseFloat(v) || 0).toFixed(2).replace('.', ',') + ' €';

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const STATUS = {
  paid:                 { label: 'Viré',        color: 'success' },
  processing:           { label: 'En cours',    color: 'warning' },
  included_in_payout:   { label: 'Inclus',      color: 'info'    },
  pending:              { label: 'En attente',  color: 'default' },
  failed:               { label: 'Echoué',      color: 'error'   },
};

function StatusChip({ status }) {
  const s = STATUS[status] || { label: status, color: 'default' };
  return <Chip label={s.label} color={s.color} size="small" />;
}

function StatCard({ label, value, sub, icon, highlight }) {
  return (
    <Card sx={highlight ? {
      background: `linear-gradient(135deg, ${C.purple} 0%, #33154f 100%)`, color: '#fff',
    } : {}}>
      <CardContent sx={{ py: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="caption" sx={{ opacity: highlight ? 0.75 : 1 }} color={highlight ? 'inherit' : 'text.secondary'}>
              {label}
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }} color={highlight ? 'inherit' : undefined}>
              {value}
            </Typography>
            {sub && (
              <Typography variant="caption" sx={{ opacity: 0.7 }} color={highlight ? 'inherit' : 'text.secondary'}>
                {sub}
              </Typography>
            )}
          </Box>
          <Box sx={{ opacity: 0.4, mt: 0.5 }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function PartnerPayouts() {
  const navigate = useNavigate();

  const [payouts,      setPayouts]      = useState([]);
  const [earnings,     setEarnings]     = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [page,         setPage]         = useState(1);
  const [totalPages,   setTotalPages]   = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [error,        setError]        = useState(null);

  const loadEarnings = useCallback(async () => {
    try {
      const r = await api.get('/connect/my-earnings');
      setEarnings(r.data?.earnings || null);
    } catch { /* non bloquant */ }
  }, []);

  const loadPayouts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (statusFilter) params.status = statusFilter;
      const r = await api.get('/partners/payouts', { params });
      setPayouts(r.data?.payouts || []);
      setTotalPages(r.data?.totalPages || 1);
    } catch (e) {
      setError('Impossible de charger les virements.');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => { loadEarnings(); }, [loadEarnings]);
  useEffect(() => { loadPayouts(); },  [loadPayouts]);

  const totalNet    = payouts.reduce((s, p) => s + (parseFloat(p.net_amount)        || 0), 0);
  const totalGross  = payouts.reduce((s, p) => s + (parseFloat(p.gross_amount)      || 0), 0);
  const totalComm   = payouts.reduce((s, p) => s + (parseFloat(p.commission_amount) || 0), 0);
  const paidCount   = payouts.filter(p => p.status === 'paid').length;

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', pb: 6 }}>

      {/* Header */}
      <Box sx={{ bgcolor: C.purple, color: '#fff', py: 4 }}>
        <Container maxWidth="xl">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ color: 'rgba(255,255,255,0.75)', mb: 2, '&:hover': { color: '#fff' } }}
          >
            Retour
          </Button>
          <Typography variant="h4" fontWeight={800} textTransform="uppercase" letterSpacing="0.03em">
            Mes virements
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.65, mt: 0.5 }}>
            Historique complet de vos versements Usafiri
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: -3 }}>

        {/* Résumé gains */}
        {earnings && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <StatCard
                label="Total encaissé"
                value={fmt(earnings.total_earned)}
                sub={`${earnings.total_orders} commandes`}
                icon={<TrendingUpIcon />}
                highlight
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                label="Commission Usafiri"
                value={fmt(earnings.total_commission)}
                sub="Déduite automatiquement"
                icon={<AccountBalanceIcon />}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                label="Virements réussis"
                value={earnings.paid_count}
                sub={earnings.failed_count > 0 ? `${earnings.failed_count} échoué(s)` : 'Aucun échec'}
                icon={<CheckCircleIcon />}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                label="Aujourd'hui (net)"
                value={fmt(earnings.today_net)}
                sub={`${earnings.today_orders} commande(s)`}
                icon={<PendingActionsIcon />}
              />
            </Grid>
          </Grid>
        )}

        {/* Tableau */}
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
              <Typography variant="h6" fontWeight={700}>
                Historique des virements
              </Typography>
              <Box display="flex" gap={2} alignItems="center">
                <TextField
                  select
                  size="small"
                  label="Statut"
                  value={statusFilter}
                  onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="paid">Virés</MenuItem>
                  <MenuItem value="processing">En cours</MenuItem>
                  <MenuItem value="pending">En attente</MenuItem>
                  <MenuItem value="failed">Échoués</MenuItem>
                </TextField>
              </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
              <Box>
                {[1, 2, 3, 4, 5].map(i => (
                  <Skeleton key={i} variant="rectangular" height={52} sx={{ mb: 1, borderRadius: 1 }} />
                ))}
              </Box>
            ) : payouts.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <AccountBalanceIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Aucun virement
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Vos virements apparaîtront ici après votre première semaine d'activité.
                </Typography>
              </Box>
            ) : (
              <>
                {/* Sous-totaux de la page */}
                {payouts.length > 0 && (
                  <Box
                    display="flex" gap={4} flexWrap="wrap" mb={2}
                    sx={{ bgcolor: 'grey.50', borderRadius: 1, px: 2, py: 1.5 }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">Brut (page)</Typography>
                      <Typography variant="body2" fontWeight={600}>{fmt(totalGross)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Commission</Typography>
                      <Typography variant="body2" fontWeight={600} color="error.main">-{fmt(totalComm)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Net (page)</Typography>
                      <Typography variant="body2" fontWeight={700} color="success.dark">{fmt(totalNet)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Virés (page)</Typography>
                      <Typography variant="body2" fontWeight={600}>{paidCount} / {payouts.length}</Typography>
                    </Box>
                  </Box>
                )}

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Période</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Commandes</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Brut</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Commission</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Net reçu</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Statut</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Date virement</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payouts.map(p => (
                        <TableRow key={p.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {fmtDate(p.period_start)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              au {fmtDate(p.period_end)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={p.order_count || 0} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">{fmt(p.gross_amount)}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="error.main">
                              -{fmt(p.commission_amount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={700} color={p.status === 'paid' ? 'success.dark' : 'text.primary'}>
                              {fmt(p.net_amount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <StatusChip status={p.status} />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="text.secondary">
                              {fmtDate(p.paid_at)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {totalPages > 1 && (
                  <Box display="flex" justifyContent="center" mt={3}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={(_, v) => setPage(v)}
                      color="primary"
                    />
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Info virement */}
        <Alert severity="info" sx={{ mt: 3 }}>
          Les virements sont générés chaque lundi pour la semaine précédente (lundi → dimanche).
          Le délai de réception sur votre compte bancaire est de 1 à 3 jours ouvrés.
        </Alert>
      </Container>
    </Box>
  );
}
